<script setup>
import { ref } from 'vue';
import { useQuasar } from 'quasar';
import { useRsvpStore } from '@/stores/rsvp/rsvp.store';
import Tr from '@/i18n/translation';

const quasar = useQuasar();
const rsvpStore = useRsvpStore();

const mainColor = rsvpStore.getColors.mainColor;
const backgroundColor = rsvpStore.getColors.backgroundColor;

const sending = ref(false);

function name(name) {
  return name !== ''
    ? name
    : Tr.t('appCore.apps.rsvp.components.form.main.name');
}

function surname(surname) {
  return surname !== ''
    ? surname
    : Tr.t('appCore.apps.rsvp.components.form.main.surname');
}

function presence(presence) {
  return presence
    ? Tr.t('appCore.apps.rsvp.components.form.main.present')
    : Tr.t('appCore.apps.rsvp.components.form.main.notPresent');
}

async function send() {
  if (rsvpStore.validate()) {
    sending.value = true;
    const err = await rsvpStore.send();
    sending.value = false;

    if (err) {
      quasar.notify({
        type: 'negative',
        message: Tr.t('appCore.apps.rsvp.components.form.main.notify.error'),
      });
    } else {
      quasar.notify({
        type: 'positive',
        message: Tr.t('appCore.apps.rsvp.components.form.main.notify.positive'),
      });
    }
  } else {
    quasar.notify({
      type: 'negative',
      message: Tr.t('appCore.apps.rsvp.components.form.main.notify.notFilled'),
    });
  }
}
</script>

<template>
  <div class="main-form">
    <transition-group name="fade">
      <div v-if="rsvpStore.getNumberOfGuests > 0" class="main-form">
        <div
          v-for="(guest, id) in rsvpStore.guestData"
          :id="`rsvp-form-${id}`"
          :key="id"
          class="rsvp-element"
        >
          <router-link
            class="guest"
            :to="
              Tr.i18nRoute({
                name: 'RsvpGuestForm',
                params: { guestId: id },
                hash: `#rsvp-form`,
              })
            "
          >
            <p class="border">
              {{ name(guest.name) }} {{ surname(guest.surname) }}
            </p>
            <p class="border presence">{{ presence(guest.presence) }}</p>
            <button class="edit">
              {{ $t('appCore.apps.rsvp.components.form.main.edit') }}
            </button>
          </router-link>

          <button class="edit bin" @click="rsvpStore.removeGuest(id)">
            <!-- Vectors and icons by Owl Icons in CC Attribution License via SVG Repo -->
            <svg
              width="35px"
              height="35px"
              viewBox="-0.5 0 19 19"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
            >
              <g
                id="out"
                stroke="none"
                stroke-width="1"
                fill="none"
                fill-rule="evenodd"
                sketch:type="MSPage"
              >
                <path
                  id="path"
                  d="M4.91666667,14.8888889 C4.91666667,15.3571429 5.60416667,16 6.0625,16 L12.9375,16 C13.3958333,16 14.0833333,15.3571429 14.0833333,14.8888889 L14.0833333,6 L4.91666667,6 L4.91666667,14.8888889 L4.91666667,14.8888889 L4.91666667,14.8888889 Z M15,3.46500003 L12.5555556,3.46500003 L11.3333333,2 L7.66666667,2 L6.44444444,3.46500003 L4,3.46500003 L4,4.93000007 L15,4.93000007 L15,3.46500003 L15,3.46500003 L15,3.46500003 Z"
                  :fill="mainColor"
                  sketch:type="MSShapeGroup"
                ></path>
              </g>
            </svg>
          </button>
        </div>
        <router-link
          :to="
            Tr.i18nRoute({
              name: 'RsvpGuestForm',
              params: { guestId: rsvpStore.getNumberOfGuests },
              hash: `#rsvp-form`,
            })
          "
          class="core-default-form-button form-button form-button-reverse"
        >
          <div class="plus plus-reverse">
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                id="+"
                d="M6.71972 0.124023V4.62108H10.9864V6.62831H6.71972V11.1473H4.52604V6.62831H0.28125V4.62108H4.52604V0.124023H6.71972Z"
                :fill="mainColor"
              />
            </svg>
          </div>
          {{ $t('appCore.apps.rsvp.components.form.main.addAnotherGuest') }}
        </router-link>

        <button
          class="core-default-form-button form-button"
          :disabled="sending"
          @click="send()"
        >
          {{ $t('appCore.apps.rsvp.components.form.main.sendRsvp') }}
        </button>
      </div>
      <div v-else class="empty-guest-list">
        <router-link
          :to="Tr.i18nRoute({ name: 'RsvpGuestForm', params: { guestId: 0 } })"
          class="core-default-form-button form-button"
        >
          <div class="plus">
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                id="+"
                d="M6.71972 0.124023V4.62108H10.9864V6.62831H6.71972V11.1473H4.52604V6.62831H0.28125V4.62108H4.52604V0.124023H6.71972Z"
                :fill="backgroundColor"
              />
            </svg>
          </div>
          {{ $t('appCore.apps.rsvp.components.form.main.addGuest') }}
        </router-link>
      </div>
    </transition-group>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styleConstans';

.main-form {
  position: relative;
  margin: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 28px;
  text-align: center;
  justify-content: center;

  @media screen and (max-width: $media-phone) {
    gap: 17px;
  }

  .rsvp-element {
    display: flex;
    flex-direction: row;
    gap: 5px 15px;
    justify-content: center;

    @media screen and (max-width: $media-phone) {
      flex-wrap: wrap;
    }
  }

  .bin {
    align-self: center;

    &:hover {
      cursor: pointer;
    }

    svg {
      width: auto;
      height: auto;

      @media screen and (max-width: $media-phone) {
        width: 30px;
        height: 30px;
      }
    }
  }

  .guest {
    display: flex;
    flex-direction: row;
    gap: 7px;
    justify-content: space-between;
    align-items: center;
    width: calc(100% - 46px);
    padding: 10px 15px;
    border: 1px solid v-bind(mainColor);
    border-radius: 15px;
    color: v-bind(mainColor);

    @media screen and (max-width: $media-phone) {
      flex-direction: column;
    }

    &:hover {
      cursor: auto;
    }

    .border {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      border: none;
      border-right: 1px solid v-bind(mainColor);
      padding-right: 7px;
      font-family: Lato, sans-serif;
      font-size: 1.1em;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      letter-spacing: 0.8px;

      @media screen and (max-width: 500px) {
        border-right: none;
        border-bottom: 1px solid v-bind(mainColor);
        width: 100%;
        padding-right: 0;
        padding-bottom: 7px;
      }
    }

    .presence {
      font-style: italic;
      opacity: 0.6;
    }

    .edit {
      padding: 4px 10px;
      border-radius: 10px;
      font-size: 1.2em;
      font-weight: 700;
      text-decoration-line: underline;

      &:hover {
        transition: 1s ease all;
        background-color: v-bind(mainColor);
        color: v-bind(backgroundColor);
      }
    }
  }

  .form-button {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 15px;
    min-width: 250px;
    border: 2px solid v-bind(mainColor);
    background-color: v-bind(mainColor);
    color: v-bind(backgroundColor);
    font-family: Lato, sans-serif;
    font-size: 1.5em;
    letter-spacing: 0.7px;

    &:hover {
      background-color: v-bind(backgroundColor);
      color: v-bind(mainColor);
      cursor: pointer;
    }

    @media screen and (max-width: $media-phone) {
      font-size: 1.25em;
    }
  }

  .form-button-reverse {
    border: none;
    text-decoration-line: underline;
    background-color: v-bind(backgroundColor);
    color: v-bind(mainColor);

    &:hover {
      transition: 1s ease all;
      background-color: v-bind(mainColor);
      color: v-bind(backgroundColor);
    }
  }
}

.empty-guest-list {
  display: flex;
  justify-content: center;
}

.plus {
  display: none;
  justify-content: center;
  align-items: center;
  border: 2px solid v-bind(backgroundColor);
  border-radius: 66px;
  min-width: 30px;
  min-height: 30px;

  @media screen and (max-width: $media-phone) {
    display: flex;
  }

  &:hover {
    background-color: v-bind(backgroundColor);
    color: v-bind(mainColor);
  }
}

.plus-reverse {
  border: 2px solid v-bind(mainColor);

  &:hover {
    background-color: v-bind(backgroundColor);
    color: v-bind(mainColor);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
